import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, HStack, Icon, Badge, Table, Thead, Tbody, Tr, Th, Td } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { FaUniversity, FaPercentage, FaChartPie, FaFileAlt } from "react-icons/fa";

const FinanceWealthSMSF: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/wealth/smsf"
        fallbackTitle="SMSF Wealth Strategy"
        fallbackDescription="Build retirement wealth through self-managed superannuation — property, tax efficiency, and compliance expertise."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              One Finance Australia
            </Text>
            <Heading size="2xl" fontWeight="bold">
              SMSF Wealth Strategy
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Build retirement wealth through self-managed superannuation — property, tax efficiency, and expert compliance guidance.</Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.xl" py={20} flex="1">
        <VStack spacing={16}>
          {/* Introduction */}
          <Box textAlign="center" maxW="3xl" mx="auto">
            <Badge colorScheme="purple" fontSize="md" px={4} py={2} borderRadius="full" mb={4}>
              Self-Managed Super Fund
            </Badge>
            <Heading size="xl" mb={4} color="#141C49">SMSF Wealth Strategy</Heading>
            <Text fontSize="lg" color="gray.600">
              Take control of your retirement with a Self-Managed Super Fund. Build wealth through property,
              shares, and other investments while enjoying significant tax advantages.
            </Text>
          </Box>

          {/* SMSF Benefits Grid */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
            <Box bg="purple.50" p={8} borderRadius="xl" border="2px solid" borderColor="purple.200">
              <HStack mb={4}>
                <Icon as={FaUniversity} boxSize={8} color="purple.600" />
                <Heading size="md" color="purple.800">Investment Control</Heading>
              </HStack>
              <VStack align="start" spacing={2}>
                <Text color="gray.700">• Direct property ownership within super</Text>
                <Text color="gray.700">• Choose your own investments and strategies</Text>
                <Text color="gray.700">• Access wholesale investment opportunities</Text>
                <Text color="gray.700">• Combine with business real property</Text>
              </VStack>
            </Box>

            <Box bg="green.50" p={8} borderRadius="xl" border="2px solid" borderColor="green.200">
              <HStack mb={4}>
                <Icon as={FaPercentage} boxSize={8} color="green.600" />
                <Heading size="md" color="green.800">Tax Benefits</Heading>
              </HStack>
              <VStack align="start" spacing={2}>
                <Text color="gray.700">• Maximum 15% tax on earnings</Text>
                <Text color="gray.700">• 10% tax on capital gains (held 12+ months)</Text>
                <Text color="gray.700">• Tax-free pension phase income</Text>
                <Text color="gray.700">• Concessional contribution deductions</Text>
              </VStack>
            </Box>
          </SimpleGrid>

          {/* SMSF Contribution Limits Table */}
          <Box w="full" bg="white" borderRadius="xl" shadow="lg" overflow="hidden" border="1px solid" borderColor="gray.200">
            <Box bg="purple.600" p={4}>
              <Heading size="md" color="white" textAlign="center">SMSF Contribution Limits 2025–26</Heading>
            </Box>
            <Table variant="striped" colorScheme="purple">
              <Thead>
                <Tr>
                  <Th>Contribution Type</Th>
                  <Th>Annual Limit</Th>
                  <Th>Tax Treatment</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td fontWeight={600}>Concessional (Pre-tax)</Td>
                  <Td>$30,000</Td>
                  <Td>Taxed at 15% in fund</Td>
                </Tr>
                <Tr>
                  <Td fontWeight={600}>Non-Concessional (After-tax)</Td>
                  <Td>$120,000</Td>
                  <Td>No tax on contribution</Td>
                </Tr>
                <Tr>
                  <Td fontWeight={600}>Bring-Forward Rule</Td>
                  <Td>$360,000 (3 years)</Td>
                  <Td>Available if under 75</Td>
                </Tr>
                <Tr>
                  <Td fontWeight={600}>Downsizer Contribution</Td>
                  <Td>$300,000 per person</Td>
                  <Td>From home sale (65+)</Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>

          {/* Investment Strategies */}
          <Box w="full">
            <Heading size="lg" textAlign="center" mb={8}>Popular SMSF Investment Strategies</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Box bg="white" p={6} borderRadius="lg" border="1px solid" borderColor="purple.200" shadow="md">
                <Icon as={FaChartPie} boxSize={10} color="purple.500" mb={3} />
                <Heading size="sm" mb={2} color="purple.800">Property Investment</Heading>
                <Text fontSize="sm" color="gray.600">
                  Purchase residential or commercial property using SMSF borrowing arrangements (LRBA).
                  Build equity in tax-advantaged environment.
                </Text>
              </Box>
              <Box bg="white" p={6} borderRadius="lg" border="1px solid" borderColor="blue.200" shadow="md">
                <Icon as={FaChartPie} boxSize={10} color="blue.500" mb={3} />
                <Heading size="sm" mb={2} color="blue.800">Share Portfolio</Heading>
                <Text fontSize="sm" color="gray.600">
                  Invest in Australian and international shares with franking credit benefits.
                  Low-cost ETFs and direct shareholdings.
                </Text>
              </Box>
              <Box bg="white" p={6} borderRadius="lg" border="1px solid" borderColor="green.200" shadow="md">
                <Icon as={FaFileAlt} boxSize={10} color="green.500" mb={3} />
                <Heading size="sm" mb={2} color="green.800">Business Real Property</Heading>
                <Text fontSize="sm" color="gray.600">
                  Hold your business premises in SMSF and lease to your company.
                  Tax-effective wealth accumulation strategy.
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Setup Process */}
          <Box w="full" bg="purple.50" p={10} borderRadius="xl">
            <Heading size="lg" textAlign="center" mb={6} color="purple.800">SMSF Setup Process</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <VStack align="start" spacing={3}>
                <HStack>
                  <Box bg="purple.600" color="white" w={8} h={8} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="sm" fontWeight={700}>1</Box>
                  <Text fontWeight={600}>Trust Deed & Structure Setup</Text>
                </HStack>
                <HStack>
                  <Box bg="purple.600" color="white" w={8} h={8} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="sm" fontWeight={700}>2</Box>
                  <Text fontWeight={600}>ATO Registration & TFN</Text>
                </HStack>
                <HStack>
                  <Box bg="purple.600" color="white" w={8} h={8} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="sm" fontWeight={700}>3</Box>
                  <Text fontWeight={600}>Bank Account & ABN</Text>
                </HStack>
              </VStack>
              <VStack align="start" spacing={3}>
                <HStack>
                  <Box bg="purple.600" color="white" w={8} h={8} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="sm" fontWeight={700}>4</Box>
                  <Text fontWeight={600}>Investment Strategy Document</Text>
                </HStack>
                <HStack>
                  <Box bg="purple.600" color="white" w={8} h={8} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="sm" fontWeight={700}>5</Box>
                  <Text fontWeight={600}>Rollover Existing Super</Text>
                </HStack>
                <HStack>
                  <Box bg="purple.600" color="white" w={8} h={8} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="sm" fontWeight={700}>6</Box>
                  <Text fontWeight={600}>Start Investing</Text>
                </HStack>
              </VStack>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceWealthSMSF;
